import { useState, useEffect } from 'react';
import { ProjectsDashboard } from './ProjectsDashboard';
import { ProjectDetails } from './ProjectDetails';
import { CreateEditProject } from './CreateEditProject';
import { Project } from './ProjectCard';
import { projectsService } from '../../../services/projects';
import { useToast } from '../../../hooks/useToast';

import { Briefcase } from 'lucide-react';

type ViewState = 
  | { type: 'dashboard' }
  | { type: 'details'; project: Project }
  | { type: 'create' }
  | { type: 'edit'; project: Project };

export function ProjectsContainer() {
  const [viewState, setViewState] = useState<ViewState>({ type: 'dashboard' });
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { addToast } = useToast();

  // Load projects from Supabase on mount
  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setIsLoading(true);
      const projectsData = await projectsService.getProjects();
      setProjects(projectsData);
    } catch (error) {
      console.error('Error loading projects:', error);
      addToast('Failed to load projects', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateProject = () => {
    setViewState({ type: 'create' });
  };

  const handleViewProject = (project: Project) => {
    setViewState({ type: 'details', project });
  };

  const handleEditProject = (project: Project) => {
    setViewState({ type: 'edit', project });
  };

  const handleSaveProject = async (projectData: Partial<Project>) => {
    try {
      if (projectData.id) {
        // Update existing project
        const updatedProject = await projectsService.updateProject({
          id: projectData.id,
          ...projectData,
          deadline: projectData.deadline ? new Date(projectData.deadline).toISOString() : undefined
        });
        setProjects(prev => prev.map(p => 
          p.id === projectData.id ? updatedProject : p
        ));
        addToast('Project updated successfully', 'success');
      } else {
        // Create new project - ensure all required fields are present
        if (!projectData.title || !projectData.company || !projectData.role || 
            !projectData.department || !projectData.location) {
          throw new Error('Missing required project fields');
        }

        const newProject = await projectsService.createProject({
          title: projectData.title,
          company: projectData.company,
          role: projectData.role,
          department: projectData.department,
          location: projectData.location,
          status: projectData.status || 'draft',
          priority: projectData.priority || 'medium',
          deadline: projectData.deadline ? new Date(projectData.deadline).toISOString() : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          budget: projectData.budget || { min: 0, max: 0, currency: 'USD' },
          team: projectData.team || [],
          requirements: projectData.requirements || { experience: '', skills: [], education: '' }
        });
        
        setProjects(prev => [newProject, ...prev]);
        addToast('Project created successfully', 'success');
      }
      
      setViewState({ type: 'dashboard' });
    } catch (error) {
      console.error('Error saving project:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      addToast(`Failed to save project: ${errorMessage}`, 'error');
      throw error; // Re-throw so the form can handle it
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    try {
      await projectsService.deleteProject(projectId);
      setProjects(prev => prev.filter(p => p.id !== projectId));
      setViewState({ type: 'dashboard' });
      addToast('Project deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting project:', error);
      addToast('Failed to delete project', 'error');
    }
  };

  const handleDuplicateProject = async (project: Project) => {
    try {
      const duplicatedProject = await projectsService.createProject({
        title: `${project.title} (Copy)`,
        company: project.company,
        role: project.role,
        department: project.department,
        location: project.location,
        status: 'draft',
        priority: project.priority,
        deadline: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        budget: project.budget,
        team: project.team,
        requirements: project.requirements
      });
      
      setProjects(prev => [duplicatedProject, ...prev]);
      addToast('Project duplicated successfully', 'success');
    } catch (error) {
      console.error('Error duplicating project:', error);
      addToast('Failed to duplicate project', 'error');
    }
  };

  const handleBackToDashboard = () => {
    setViewState({ type: 'dashboard' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-16">
        <div className="text-center space-y-6">
          <div className="flex items-center justify-center">
            <div className="h-16 w-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-xl flex items-center justify-center shadow-lg shadow-slate-900/20">
              <Briefcase className="h-8 w-8 text-white animate-pulse" />
            </div>
          </div>
          <div className="space-y-3">
            <h3 className="text-xl font-bold text-slate-900">Loading Projects</h3>
            <p className="text-slate-600 font-medium">Setting up your hiring workspace...</p>
          </div>
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-slate-900"></div>
          </div>
        </div>
      </div>
    );
  }

  // Render the appropriate view based on current state
  switch (viewState.type) {
    case 'dashboard':
      return (
        <ProjectsDashboard
          projects={projects}
          onCreateProject={handleCreateProject}
          onViewProject={handleViewProject}
          onEditProject={handleEditProject}
          onDeleteProject={handleDeleteProject}
          onDuplicateProject={handleDuplicateProject}
        />
      );

    case 'details':
      return (
        <ProjectDetails
          project={viewState.project}
          onBack={handleBackToDashboard}
          onEdit={() => handleEditProject(viewState.project)}
        />
      );

    case 'create':
      return (
        <CreateEditProject 
          onSave={handleSaveProject}
          onCancel={handleBackToDashboard}
        />
      );

    case 'edit':
      return (
        <CreateEditProject 
          project={viewState.project}
          onSave={handleSaveProject}
          onCancel={handleBackToDashboard}
        />
      );

    default:
      return (
        <ProjectsDashboard
          projects={projects}
          onCreateProject={handleCreateProject}
          onViewProject={handleViewProject}
          onEditProject={handleEditProject}
          onDeleteProject={handleDeleteProject}
          onDuplicateProject={handleDuplicateProject}
        />
      );
  }
} 